import React, { useState, useRef, useEffect } from "react";
import { Spinner } from "../common/Spinner";
import { useLocationStore } from "../../store/location.store";
import { useGoogleMapsLoader } from "../../hooks/useGoogleMapsLoader";

import {
  getUserLocation,
  getAddressSuggestions,
  getPlaceDetails,
} from "../../utils/distance";
import { toast } from "react-hot-toast";
import { detectCommunityOrder } from "../../utils/communityDetection";
import { useAuth } from "../../context/AuthProvider";
import { updateUserProfile } from "../../services/user.service";
import CommunityAddressSheet, {
  BNL_FALLBACK,
  CommunityAddressResult,
} from "./CommunityAddressSheet";
import { getCommunityById } from "../../services/community.service";
import type { Community } from "../../types";

// Icons
const IconCompass = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="28"
    height="28"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className="text-cyan-300 drop-shadow-[0_0_8px_rgba(34,211,238,0.8)]"
  >
    <circle cx="12" cy="12" r="10" />
    <polygon points="16.24 7.76 14.12 14.12 7.76 16.24 9.88 9.88 16.24 7.76" />
  </svg>
);

const IconMapPin = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="28"
    height="28"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className="text-purple-300 drop-shadow-[0_0_8px_rgba(216,180,254,0.8)]"
  >
    <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z" />
    <circle cx="12" cy="10" r="3" />
  </svg>
);

declare global {
  interface Window {
    google: any;
  }
}

export const LocationModal: React.FC = () => {
  const { isLoaded, loadError } = useGoogleMapsLoader();
  const { isPromptOpen, setLocation, setPromptOpen } = useLocationStore();
  const { user } = useAuth();
  
  const [step, setStep] = useState<"select" | "manual" | "map">("select");
  const [manualAddress, setManualAddress] = useState("");
  const [suggestions, setSuggestions] = useState<any[]>([]);
  const [isGeocoding, setIsGeocoding] = useState(false);
  const [mapCenter, setMapCenter] = useState<{ lat: number; lng: number } | null>(null);
  const [selectedLabel, setSelectedLabel] = useState("");
  const [showCommunitySheet, setShowCommunitySheet] = useState(false);
  const [pendingCommunity, setPendingCommunity] = useState<Community | null>(null);
  const [pendingLoc, setPendingLoc] = useState<{ lat: number; lng: number } | null>(null);
  const [pendingLabel, setPendingLabel] = useState("");

  const mapRef = useRef<HTMLDivElement>(null);
  const googleMapRef = useRef<google.maps.Map | null>(null);
  const debounceTimeout = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    const initMap = () => {
      console.log("INIT MAP DEBUG", {
        step,
        isLoaded,
        mapCenter,
        hasMapRef: !!mapRef.current,
      });

      if (step !== "map" || !isLoaded || !mapRef.current || !mapCenter) return;

      try {
        if (!googleMapRef.current) {
          const mapOptions: google.maps.MapOptions = {
            center: mapCenter,
            zoom: 16,
            disableDefaultUI: true,
            mapTypeControl: false,
            streetViewControl: false,
            fullscreenControl: false,
            styles: [
              { elementType: "geometry", stylers: [{ color: "#1a1a2e" }] },
              { elementType: "labels.text.stroke", stylers: [{ color: "#1a1a2e" }] },
              { elementType: "labels.text.fill", stylers: [{ color: "#a0a0a0" }] },
              {
                featureType: "road",
                elementType: "geometry",
                stylers: [{ color: "#2a2a3e" }],
              },
              {
                featureType: "road",
                elementType: "geometry.stroke",
                stylers: [{ color: "#1a1a2e" }],
              },
              {
                featureType: "road.highway",
                elementType: "geometry",
                stylers: [{ color: "#3a3a4e" }],
              },
              {
                featureType: "water",
                elementType: "geometry",
                stylers: [{ color: "#0d1b2a" }],
              },
              {
                featureType: "poi",
                elementType: "labels.text.fill",
                stylers: [{ color: "#6b6b8b" }],
              },
            ],
          };

          const map = new google.maps.Map(mapRef.current, mapOptions);
          googleMapRef.current = map;

          console.log("Map initialized successfully", map);

          map.addListener("idle", () => {
            const center = map.getCenter();
            if (center) {
              const newCenter = { lat: center.lat(), lng: center.lng() };
              console.log("Map center updated:", newCenter);
              setMapCenter(newCenter);
            }
          });

          google.maps.event.addListenerOnce(map, 'tilesloaded', () => {
            console.log("Map tiles loaded successfully");
          });
        } else {
          googleMapRef.current.panTo(mapCenter);
        }
      } catch (err) {
        console.error("MAP INIT ERROR", err);
        toast.error("Map failed to render. Please try again later.");
      }
    };

    if (step === "map") {
      initMap();
    } else {
      googleMapRef.current = null;
    }
  }, [step, mapCenter, isLoaded]);

  if (!isPromptOpen) return null;

  if (step === "map") {
    if (loadError) {
      return (
        <div className="fixed inset-0 bg-black/90 backdrop-blur-sm z-[100] flex items-center justify-center p-4">
          <div className="text-cyan-300 font-mono text-sm">
            Map failed to load. Please try again.
          </div>
        </div>
      );
    }
    if (!isLoaded) {
      return (
        <div className="fixed inset-0 bg-black/90 backdrop-blur-sm z-[100] flex items-center justify-center p-4">
          <div className="text-cyan-300 font-mono text-sm">Loading map…</div>
        </div>
      );
    }
  }

  const handleUseGPS = async () => {
    try {
      setIsGeocoding(true);
      const coords = await getUserLocation(); 
      setMapCenter(coords);
      setSelectedLabel("Current Location");
      setStep("map");
      toast.success("Location set via GPS");
    } catch (error) {
      toast.error("GPS Signal Lost. Switch to Manual.");
      setStep("manual");
    } finally {
      setIsGeocoding(false);
    }
  };

  const handleAddressChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setManualAddress(value);

    if (debounceTimeout.current) clearTimeout(debounceTimeout.current);
    if (value.length < 3) {
      setSuggestions([]);
      return;
    }

    debounceTimeout.current = setTimeout(async () => {
      try {
        const results = await getAddressSuggestions(value);
        setSuggestions(results);
      } catch (error) {
        console.error(error);
      }
    }, 300);
  };

  const handleSelectSuggestion = async (suggestion: any) => {
    try {
      setIsGeocoding(true);
      const details = await getPlaceDetails(suggestion.place_id);
      const label = suggestion.structured_formatting?.main_text || details.address.split(",")[0];
      
      setMapCenter({ lat: details.lat, lng: details.lng });
      setSelectedLabel(label);
      setSuggestions([]);
      setManualAddress("");
      setStep("map");
      toast.success("Location set!");
    } catch (error) {
      toast.error("Target acquisition failed.");
    } finally {
      setIsGeocoding(false);
    }
  };

  const finishLocationSave = async (
    label: string,
    center: { lat: number; lng: number },
    communityMatch: { isCommunity: boolean; communityId?: string; communityName?: string },
    metaExtra?: Record<string, any>
  ) => {
    setLocation(center, label, {
      id: communityMatch.communityId,
      name: communityMatch.communityName,
      isCommunity: communityMatch.isCommunity,
    });
    if (user?.uid) {
      try {
        await updateUserProfile(user.uid, {
          address: label,
          homeAddress: label,
          homeAddressMeta: {
            mode: communityMatch.isCommunity ? "community" : "other",
            communityId: communityMatch.communityId,
            communityName: communityMatch.communityName,
            location: center,
            rawAddressFromMap: selectedLabel || label,
            ...(metaExtra || {}),
          },
        });
      } catch (err) {
        console.warn("Could not save community address to profile", err);
      }
    }
    setPromptOpen(false);
    setStep("select");
    setShowCommunitySheet(false);
    toast.success("COORDINATES LOCKED");
  };

  const handleConfirmLocation = async () => {
    if (!mapCenter) return;
    const communityMatch = await detectCommunityOrder({
      address: selectedLabel,
      location: mapCenter,
    });
    const isBnl =
      communityMatch.isCommunity &&
      (communityMatch.communityId === "9lSkwTx49zGTNRHlOOHb" ||
        /bren northern|bnl/i.test(communityMatch.communityName || selectedLabel || ""));

    if (isBnl) {
      let communityDoc: Community | null = null;
      try {
        communityDoc = await getCommunityById("9lSkwTx49zGTNRHlOOHb");
      } catch {
        communityDoc = null;
      }
      setPendingCommunity(communityDoc || BNL_FALLBACK);
      setPendingLoc(mapCenter);
      setPendingLabel(selectedLabel);
      setShowCommunitySheet(true);
      return;
    }

    if (communityMatch.isCommunity) {
      toast.success(`Community delivery: ${communityMatch.communityName}`);
    }
    await finishLocationSave(selectedLabel, mapCenter, communityMatch);
  };

  const handleCommunityConfirm = async (result: CommunityAddressResult) => {
    if (!pendingLoc) return;
    await finishLocationSave(result.formattedAddress, pendingLoc, {
      isCommunity: true,
      communityId: result.homeAddressMeta.communityId,
      communityName: result.homeAddressMeta.communityName,
    }, result.homeAddressMeta);
    toast.success("Community flat saved");
  };

  const handleCommunitySkip = async () => {
    if (!pendingLoc) return;
    await finishLocationSave(pendingLabel || selectedLabel, pendingLoc, {
      isCommunity: true,
      communityId: "9lSkwTx49zGTNRHlOOHb",
      communityName: "Bren Northern Lights",
    });
  };

  return (
    <>
    <CommunityAddressSheet
      open={showCommunitySheet}
      community={pendingCommunity}
      location={pendingLoc}
      rawAddress={pendingLabel}
      onConfirm={handleCommunityConfirm}
      onSkip={handleCommunitySkip}
      onClose={() => setShowCommunitySheet(false)}
    />
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[100] flex items-center justify-center p-4">
      <div className="relative w-full max-w-4xl shadow-[0_0_50px_rgba(6,182,212,0.3)] transition-all duration-500 flex flex-col max-h-[90vh] overflow-hidden"
        style={{
          backgroundImage: "url('/modalbg.png')",
          backgroundSize: "cover",
          backgroundPosition: "center",
          minHeight: step === "map" ? "600px" : "500px",
          maxHeight: step === "map" ? "80vh" : "70vh"
        }}
      >
        <button
          onClick={() => {
            setPromptOpen(false);
            setStep("select");
          }}
          className="absolute top-6 right-6 text-cyan-400 hover:text-white font-bold text-lg tracking-widest opacity-80 hover:opacity-100 hover:drop-shadow-[0_0_8px_cyan] transition-all z-20 p-2"
        >
          ✕
        </button>

        <div className="relative z-10 w-full h-full flex flex-col p-8 md:p-12 overflow-y-auto">
          <div className="text-center mb-8">
            <h2 className="text-3xl md:text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-cyan-300 via-blue-400 to-purple-400 drop-shadow-[0_0_15px_rgba(34,211,238,0.6)] mb-3 font-mono tracking-wider">
              {step === "map" ? "Confirm Delivery Target" : "Set Your Location"}
            </h2>
            <p className="text-cyan-200/80 text-sm md:text-base tracking-wide font-medium">
              {step === "map" ? "Drag map to fine-tune your delivery spot" : "Choose how to find kitchens near you"}
            </p>
          </div>

          <div className="flex-grow flex items-center justify-center w-full mb-8">
            {step === "select" && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 w-full max-w-2xl">
                <button
                  onClick={handleUseGPS}
                  disabled={isGeocoding}
                  className="group relative w-full h-28 overflow-hidden rounded-2xl transition-all duration-300 hover:scale-[1.02] active:scale-95 shadow-2xl"
                >
                  <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/20 to-blue-600/20 rounded-2xl" />
                  <div className="absolute inset-0 border-2 border-cyan-400/50 rounded-2xl group-hover:border-cyan-300 group-hover:shadow-[0_0_20px_rgba(34,211,238,0.4)] transition-all" />
                  <div className="relative z-10 flex flex-col items-center justify-center h-full p-6 text-center">
                    {isGeocoding ? (
                      <Spinner size={24} color="#22d3ee" />
                    ) : (
                      <IconCompass />
                    )}
                    <div className="mt-3">
                      <span className="block text-cyan-100 font-bold text-lg tracking-wide drop-shadow-md">Use GPS</span>
                      <span className="text-cyan-200 text-xs font-medium tracking-wide">Current Location</span>
                    </div>
                  </div>
                </button>

                <button
                  onClick={() => setStep("manual")}
                  className="group relative w-full h-28 overflow-hidden rounded-2xl transition-all duration-300 hover:scale-[1.02] active:scale-95 shadow-2xl"
                >
                  <div className="absolute inset-0 bg-gradient-to-br from-purple-500/20 to-pink-600/20 rounded-2xl" />
                  <div className="absolute inset-0 border-2 border-purple-400/50 rounded-2xl group-hover:border-purple-300 group-hover:shadow-[0_0_20px_rgba(168,85,247,0.4)] transition-all" />
                  <div className="relative z-10 flex flex-col items-center justify-center h-full p-6 text-center">
                    <IconMapPin />
                    <div className="mt-3">
                      <span className="block text-purple-100 font-bold text-lg tracking-wide drop-shadow-md">Manual Entry</span>
                      <span className="text-purple-200 text-xs font-medium tracking-wide">Type address</span>
                    </div>
                  </div>
                </button>
              </div>
            )}

            {step === "manual" && (
              <div className="w-full max-w-lg bg-black/40 border-2 border-cyan-500/60 backdrop-blur-xl p-8 rounded-3xl shadow-2xl">
                <div className="text-center mb-6">
                  <h3 className="text-xl font-bold text-cyan-200 tracking-wide mb-2">Enter Address</h3>
                  <p className="text-cyan-300 text-sm">Start typing your area/sector</p>
                </div>
                <div className="space-y-4">
                  <input
                    type="text"
                    placeholder="eg: Sector 45, Gurgaon or MG Road..."
                    className="w-full bg-black/50 border-2 border-cyan-500/50 text-cyan-100 px-4 py-4 rounded-xl focus:outline-none focus:border-cyan-400 text-lg placeholder-cyan-500 font-mono tracking-wide"
                    value={manualAddress}
                    onChange={handleAddressChange}
                    autoFocus
                  />
                  {suggestions.length > 0 && (
                    <ul className="max-h-48 overflow-y-auto bg-black/70 border border-cyan-600/50 rounded-xl scrollbar-thin scrollbar-thumb-cyan-600/50">
                      {suggestions.map((item: any) => (
                        <li
                          key={item.place_id}
                          onClick={() => handleSelectSuggestion(item)}
                          className="px-5 py-4 hover:bg-cyan-900/30 cursor-pointer border-b border-cyan-800/30 last:border-b-0 text-sm text-cyan-200 font-mono hover:text-white transition-all"
                        >
                          {item.structured_formatting?.main_text}
                          {item.structured_formatting?.secondary_text && (
                            <span className="block text-cyan-400 text-xs mt-1 opacity-75">
                              {item.structured_formatting.secondary_text}
                            </span>
                          )}
                        </li>
                      ))}
                    </ul>
                  )}
                  <button
                    onClick={() => setStep("select")}
                    className="w-full text-cyan-300 hover:text-cyan-100 text-sm font-bold uppercase tracking-widest py-2 px-4 border border-cyan-500/50 rounded-xl hover:bg-cyan-900/20 transition-all font-mono"
                  >
                    ← Back to Options
                  </button>
                </div>
              </div>
            )}

            {step === "map" && (
              <div className="w-full h-[500px] md:h-[600px] flex flex-col">
                <div className="flex-1 relative rounded-3xl overflow-hidden border-4 border-cyan-500/60 shadow-[0_0_40px_rgba(34,211,238,0.4)] bg-gray-900">
                  <div 
                    ref={mapRef} 
                    className="absolute inset-0"
                    style={{ width: '100%', height: '100%' }}
                  />
                  
                  {/* Center Pin */}
                  <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none z-20">
                    <div className="relative">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="64"
                        height="64"
                        viewBox="0 0 24 24"
                        fill="url(#gradient)"
                        className="drop-shadow-[0_0_25px_rgba(0,213,255,0.8)] animate-pulse"
                      >
                        <defs>
                          <radialGradient id="gradient" cx="50%" cy="50%">
                            <stop offset="0%" stopColor="#00d5ff" />
                            <stop offset="70%" stopColor="#00b5ff" />
                            <stop offset="100%" stopColor="#0099ff" stopOpacity="0.8" />
                          </radialGradient>
                        </defs>
                        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm0-14c-3.31 0-6 2.69-6 6s2.69 6 6 6 6-2.69 6-6-2.69-6-6-6z" />
                      </svg>
                      <div className="absolute inset-0 bg-black/30 rounded-full blur-sm animate-ping" />
                    </div>
                  </div>
                  
                  {/* Instructions */}
                  <div className="absolute bottom-6 left-1/2 -translate-x-1/2 bg-black/80 backdrop-blur-sm text-center px-8 py-4 rounded-2xl border border-cyan-600/50 text-cyan-200 text-sm font-mono tracking-wider shadow-2xl">
                    Drag map to adjust delivery target
                    <div className="text-xs mt-1 opacity-75 font-mono tracking-[0.2em]">
                      📍 {selectedLabel}
                    </div>
                  </div>
                  
                  {/* Debug Coordinates */}
                  <div className="absolute top-6 left-6 bg-black/90 backdrop-blur-sm text-cyan-300 text-xs p-3 rounded-xl font-mono border border-cyan-700/50 shadow-lg z-30">
                    TARGET<br/>
                    {mapCenter ? `${mapCenter.lat.toFixed(6)}, ${mapCenter.lng.toFixed(6)}` : "ACQUIRING..."}
                  </div>
                </div>
                
                <div className="mt-6 flex items-center justify-center gap-6">
                  <button
                    onClick={() => setStep("select")}
                    className="px-8 py-3 text-cyan-300 hover:text-cyan-100 font-bold uppercase tracking-widest text-sm bg-black/50 border border-cyan-500/50 rounded-xl hover:bg-cyan-900/20 transition-all font-mono"
                  >
                    Re-scan
                  </button>
                  <button
                    onClick={handleConfirmLocation}
                    className="px-10 py-4 bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-black font-bold uppercase tracking-widest text-base rounded-2xl shadow-[0_0_25px_rgba(34,211,238,0.6)] hover:shadow-[0_0_35px_rgba(34,211,238,0.8)] transition-all font-mono active:scale-95"
                  >
                    LOCK TARGET
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
    </>
  );
};
